import { Component, ViewChild, ChangeDetectorRef, InjectionToken, FactoryProvider, OnDestroy, OnInit } from '@angular/core';
import { DatePipe, CurrencyPipe } from "@angular/common";
import { BaseControlComponent } from '../base-control/base-control.component';
import { DataTableComponent } from '../../../common/components/data-table/data-table.component';
import { QueryService } from '../../../common/services/query.service';
import { DataTableController } from './controllers/controller.interface';
import { DefaultController } from './controllers/default.controller';
import { WellTransferController } from './controllers/well-transfer.controller';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { Column } from '../../../common/components/data-table/models/column.interface';
import { Controllable } from '../../component-base/controller/controllable.interface';
import { RuleCompiler } from '../../../flow/services/rule-compiler.service';
import { ValueFormatter, ValueFormatterFactory } from '../../../common/components/data-table/formatters/data-table.formatters';
import { Rule } from '../../../common/models/flow.dto';
import { deepClone } from '../../../builder/models/editor.model';
import { GridOptions, ValueGetterParams, RowSelectionOptions, GridApi } from 'ag-grid-community';

import { Well } from '../../../common/models/well.model';
import { Router } from '@angular/router';
import * as jp from "jsonpath/jsonpath";
import { AlteredContextEvent, ContextInteractionService } from '../../services/context-interaction.service';
import { GridFormatterService } from "../../../common/services/grid-formatter.service";

let defaultControllerFactory = () => { return new DefaultController };
let wellTransferControllerFactory = () => { return new WellTransferController() }
export const DEFAULT_CONTROLLER = new InjectionToken<DataTableController<any>>('Default');
export const WELL_TRANSFER_CONTROLLER = new InjectionToken<DataTableController<any>>('Well Transfer');

export const FLOW_DATA_TABLE_CONTROLLERS: FactoryProvider[] = [
    {
        provide: DEFAULT_CONTROLLER, useFactory: defaultControllerFactory
    },
    {
        provide: WELL_TRANSFER_CONTROLLER, useFactory:
            wellTransferControllerFactory, deps: []
    }
];

@Component({
    selector: 'flow-data-table',
    templateUrl: './flow-data-table.component.html',
    styleUrls: ['./flow-data-table.component.scss'],
    providers: FLOW_DATA_TABLE_CONTROLLERS,
    standalone: false
})
export class FlowDataTableComponent extends BaseControlComponent implements Controllable, OnInit, OnDestroy {
    @ViewChild(DataTableComponent, { static: true })
    table: DataTableComponent;

    public _columns: Column[];
	public _gridOptions: GridOptions;
    static componentName = 'FlowDataTableComponent';
    readonly controllers = FLOW_DATA_TABLE_CONTROLLERS;
    controller: DataTableController<any>;

    set columns(val: Column[]) {
        let copy = deepClone(val) as Column[];
        let columns = [] as Column[];

        for (let prop in copy) {
            let column = copy[prop];
            if (column.formatterOptions != null && column.valueFormatter === ValueFormatter.ArraySelector) {
                column.formatterOptions.params = this._compiler.compileSyncRuleFunc(column.formatterOptions.params as Rule, "");
            }
            if (column.renderer === "details") {
                column.rendererParams = {
                    values: {
                        url: (data: any) => {
                            let builtUrlTree = this._router.parseUrl(this._router.url);
                          //  let builtUrl = builtUrlTree.root.toString() + "dashboard/view/well/" + data.id;
                            let idParam: string = data.id;
                            if (column.objectProperty != null && column.objectProperty.path != null) {
                                let dataParam = jp.value(data, column.objectProperty.path);
                                if (dataParam != null) {             
                                    idParam = dataParam;
                                }
                            }
                            let builtUrl = `${builtUrlTree.root.toString()}dashboard/view/well/${idParam}`;
                            return builtUrl;
                        },
                        openInNewWindow: true
                    }
                }
            }

            columns.push(column);
        }
        this._columns = columns;
    }

    set gridOptions(val: GridOptions) {
        this._gridOptions = val;
    }

    get gridOptions(): GridOptions {
        return this._gridOptions;
    }

    get columns() {
        return this._columns;
    }

    public _selectedItems$: BehaviorSubject<any[]> = new BehaviorSubject([]);

    set selectedItems(val: any[]) {
        this._selectedItems$.next(val);
    }

    get selectedItems(): any[] {
        return this._selectedItems$.value;
    }

    get selectedItemsObs(): Observable<any[]> {
        return this._selectedItems$.asObservable();
    }

    private _dataSource: Observable<any>;

    get dataSource(): Observable<any> {
        if (this._dataSource == null) return of([]);
        return this._dataSource;
    }

    private _dataLoaded: boolean = false;

    get dataLoaded(): boolean {
        return this._dataLoaded;
    }

    private set dataLoaded(value: boolean) {
        this._dataLoaded = value;
    }

    subscriptions: Subscription = new Subscription();

    constructor(
        public _queryService: QueryService,
        public _changeDetectorRef: ChangeDetectorRef,
        public _compiler: RuleCompiler,
        public _router: Router,
        private _formatter: GridFormatterService,
        private _datePipe: DatePipe,
        private _currencyPipe: CurrencyPipe,
        contextService: ContextInteractionService
    ) {
        super(contextService);
    }

    ngOnInit() {
        super.ngOnInit();
        if (this.dataLoaded == null) this.dataLoaded = false;
        this.columns = this.componentData.config.columns;
        const displaysSelectColumn = this.componentData.config.displaySelectColumn != null
            && this.componentData.config.displaySelectColumn;
        const initiallyDisabled = this.control.disabled || this.isInReview;
        let gridOptions = {
                onGridReady: (params) => {
                    this.table.api = params.api;
                    this.table.gridIsReady.emit(true);
                },
                headerHeight: 50,
                selectionColumnDef: this._formatter.defaultSelectionColumnDef,
                context: { selectionDisabled: initiallyDisabled }
        } as GridOptions;
        if (this.componentData.config.gridOptions.rowSelection != null) {
            let rowSelection = {
                mode: (this.componentData.config.gridOptions.rowSelection === 'multiple')
                    ? "multiRow"
                    : "singleRow",
                checkboxes: displaysSelectColumn && !initiallyDisabled,
                enableClickSelection: !initiallyDisabled
            } as RowSelectionOptions;
            gridOptions.rowSelection = rowSelection;
        }
        this.gridOptions = gridOptions;
        let query = this.componentData.config.query;

            this.subscriptions.add(this.control.statusChanges.subscribe((result) => {
                if (this.control.disabled || this.isInReview)
                    this.table.disableSelection();
                else
                    this.table.enableSelection();
                if (this.table.grid && this.table.grid.api) {
                    this.table.grid.api.sizeColumnsToFit();
                }
            }));

        if (query && query.id !== '-1') {
            let storedData = this.restoreData();
            let useRestored = !this.enableEditing && (storedData !== undefined && storedData !== null && storedData.length > 0);
            let dataObs$ = (useRestored)
                ? of(storedData).pipe(tap((data: any) => { this.dataLoaded = true; }))
                : (this.isEdit == false)
                    ? this._queryService.runQuery(query, this).pipe(
                        catchError((err) => {
                            if (err.error != null) console.log(err.error)
                            else console.error(err);
                            return of([]);
                        }),
                        map((result: any) => {
                            if (result == null || result == "") { 
                                //results from this query should always be an array of data since this is a table
                                result = [];
                            }
                            return result;
                        }),
                        tap((result) => {                           
                            this.storeResults(result);
                        }))
                    : of([]);
                

            this._dataSource = dataObs$.pipe();
            this.table.gridIsReady.subscribe({
                next: (evt: boolean) => {
                    if (evt) {
                        this.subscribeToGridEvents();
                    }
                }
            });
            
            let context$ = this.contextService.contextTargetHasBeenSet.pipe(
                tap((evt: AlteredContextEvent) => {
                    if (evt.target === `${this.componentData.contextTarget}.value`) {
                        this.updateTable();
                    }
                })
            ).subscribe({            
                next: () => { },
                error: (error) => { console.error(error); }
            });
            this.subscriptions.add(context$);
        }
        else if (!this.componentData.config.data)
            this.componentData.config.data = of([]);

        if (!this.componentData.config.columns) {
            this.componentData.config.columns = [];
            this._changeDetectorRef.detectChanges();
        }

     
    }

    subscribeToGridEvents(): void {
        this.subscriptions.add(this.table.grid.rowDataUpdated.subscribe((result) => {
                this.updateTable();
            }));
        this.subscriptions.add(this.table.onSelectedItemsChange.subscribe((items) => {
            this.setContextValue(items, false);
            this.change();
        }));
    }

    export(): void {
        if (this.dataLoaded === true && this.table != null && this.table.api != null && this._datePipe != null) {
            const now = this._datePipe.transform(new Date(), "yyyy-MM-dd")?.toString() ?? "";
            let fileName: string = `datatable_${now}`;
            const selectedOnly = this.table._options?.rowSelection != null
                && (this.table.api.getSelectedRows()?.length ?? 0) > 0;
            this._formatter.exportDataToExcel(fileName, "table data", this.table.api, selectedOnly);
        }
    }

    private updateTable(): void {
        if (this.table.gridOptions == null || this.table.gridOptions?.rowSelection == null || 
            (this.table.gridOptions.rowSelection !== 'single' && this.table.gridOptions.rowSelection !== 'multiple' &&
                (this.table.gridOptions.rowSelection as RowSelectionOptions).mode !== 'singleRow' && 
                (this.table.gridOptions.rowSelection as RowSelectionOptions).mode !== 'multiRow')) {                
                    return;
                }
            
        let selectedItems: any[] = this.getContextValue() || [];
        if (this.table.api != null) {
             this.table.api.forEachNode(node => { node.setSelected(false); }); //need to clear out any existing selections before selecting new
            this.table.api.forEachNode(node => {            
            for (let obj of selectedItems) {
                if (this.controller.objectEquals(obj, node.data)) {
                    node.setSelected(true);
                    this.control.updateValueAndValidity();
                    continue;
                }
            }
        });
        }
    }

    ngOnDestroy() {
        if (this.subscriptions != null) this.subscriptions.unsubscribe();
    }
    

    defaultConfig() {
        return {
            suppressDragLeaveHidesColumns: true
        } as GridOptions;
    }

    getColumnValue(row: any, column: any): any {
        let value = jp.value(row, column.objectProperty.path);
        if (column.valueFormatter != null) {
            //if (column.valueFormatter.toString().toLocaleLowerCase() === 'currency') {
            //    value = this._currencyPipe.transform(value, 'USD', 'symbol', '1.2-2');
            //} else if (column.valueFormatter.toString().toLocaleLowerCase() === 'date') {
            //    value = this._datePipe.transform(value, 'MM/dd/yyyy');
            //}
            let valueGetter = ValueFormatterFactory.getFunc(column.valueFormatter);
            let path = (column.objectProperty.path.startsWith("$.")) ? column.objectProperty.path.substr(2) : column.objectProperty.path;
            try {
                value = valueGetter(this.table._injector, column.formatterOptions, { colDef: { field: path }, data: row } as ValueGetterParams); 
            }
            catch (error) {
                console.log(`An error occurred when trying to fetch the value for the following path: ${path}, that error was ${error}`);
            }
        }
        return value;
    }


    restoreData(): any {
        return this.getContext("results");
    }

    storeResults(results: any): void {
        //Generate the html Table version of this
        if (results == null) {
            results = [];
        }
        let htmlVersion = this.formatResultsAsText(results);
        //If they've specified a location to store an HTML representation of the results store it there specifically. If not, store it relative to main component path.
        if (this.componentData.config.htmlResultsContextTarget && (!this.isEdit && this.componentData)) {
            this.saveContextData(this.componentData.config.htmlResultsContextTarget, htmlVersion);
        }
        else
            this.storeContext(htmlVersion, "htmlResults");

        this.storeContext(results, "results");
    }

    formatResultsAsText(results: any): string {
        if (results == null) {
            results = [];
        }
        if (results.length === undefined) {
            results = [results];
        }
        let displayResults = "";
        if (this.componentData.config.prefixText && this.componentData.config.prefixText.length > 0) {
            displayResults = this.componentData.config.prefixText;
        }
        displayResults = "<table><thead><tr>";
        for (let r = 0; r < this.columns.length; r++) {
            let resultValue = this.columns[r].name;
            displayResults += `<th>${resultValue}</th>`;
        }
        displayResults += "</tr></thead>";
        for (let r = 0; r < results.length; r++) {
            displayResults += "<tr>";
            for (let t = 0; t < this.columns.length; t++) {
                let column = this.columns[t];
                var valueGetter = ValueFormatterFactory.getFunc(column.valueFormatter);
                let path = "";

                //strip off the $. prefix as it gets automatically added by formatters
                if (column.objectProperty.path.startsWith("$."))
                    path = column.objectProperty.path.substring(2);
                else path = column.objectProperty.path;

                let value = "";
                try {
                    value = valueGetter(this.table._injector, column.formatterOptions, { colDef: { field: path }, data: results[r] } as ValueGetterParams); //jp.value(results[r], column.objectProperty.path);
                }
                catch (error) {
                    console.log(`An error occurred when trying to fetch the value for the following path: ${path}, that error was ${error}`);
                }
                displayResults += `<td>${value}</td>`;
            }
            displayResults += "</tr>";
        }

        displayResults += "</table>";

        return displayResults;
    }

    onTableDataLoaded(isLoaded: boolean): void {
        if (!isLoaded && this.isEnabled) {
            this.dataLoaded = isLoaded;
        } else {
            this.dataLoaded = isLoaded;
        }
        
    }
}
